<template>
  <div class="pd-select" :class="{ 'pd-select--open': isOpen }" ref="selectWrapper">
    <div class="pd-select__control" @click="isOpen = !isOpen">
      <span class="pd-select__value" :class="{ 'pd-select__placeholder': value === null }">
        {{ selectedLabel }}
      </span>
      <div class="pd-select__actions">
        <i v-if="clearable && value !== null" class="fa-solid fa-xmark pd-select__clear" @click.stop="clearSelection"></i>
        <i class="fa-solid fa-chevron-down pd-select__arrow"></i>
      </div>
    </div>
    <transition name="pd-fade-slide">
      <div v-if="isOpen" class="pd-select__menu">
        <div class="pd-select__menu-inner">
          <!-- Buscador interno -->
          <div class="pd-select__search" v-if="normalizedOptions.length > 8">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Buscar..." 
              @click.stop
              ref="searchInput"
            />
          </div>

          <div 
            v-for="opt in filteredOptions" 
            :key="opt.value"
            class="pd-select__option"
            :class="{ 'pd-select__option--selected': isSelected(opt) }"
            @click="selectOption(opt)"
          >
            {{ opt.label }}
            <i v-if="isSelected(opt)" class="fa-solid fa-check pd-select__check"></i>
          </div>

          <div v-if="filteredOptions.length === 0" class="pd-select__empty">
            Sin resultados
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'DashboardSelect',
  props: {
    value: { type: [String, Number], default: null },
    options: { type: Array, required: true },
    placeholder: { type: String, default: 'Seleccionar...' },
    clearable: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: ''
    };
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.searchQuery = '';
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map(o => {
        if (typeof o === 'object') return { value: String(o.value), label: o.label };
        return { value: String(o), label: String(o) };
      });
    },
    selectedValuesArray() {
      if (this.value === null || this.value === undefined || this.value === '') return [];
      return String(this.value).split(',').filter(Boolean);
    },
    selectedLabel() {
      if (this.multiple) {
        if (!this.value) return this.placeholder;
        const arr = this.selectedValuesArray;
        if (arr.length === 0) return this.placeholder;
        if (arr.length === 1) {
           const opt = this.normalizedOptions.find(o => o.value === arr[0]);
           return opt ? opt.label : this.placeholder;
        }
        return `${arr.length} seleccionados`;
      } else {
        const opt = this.normalizedOptions.find(o => o.value === String(this.value));
        return opt ? opt.label : this.placeholder;
      }
    },
    filteredOptions() {
      if (!this.searchQuery) return this.normalizedOptions;
      const lower = this.searchQuery.toLowerCase();
      return this.normalizedOptions.filter(o => o.label.toLowerCase().includes(lower));
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    clearSelection() {
      this.$emit('input', null);
      this.$emit('change', null);
      this.isOpen = false;
    },
    isSelected(opt) {
      if (this.multiple) {
        return this.selectedValuesArray.includes(opt.value);
      }
      return opt.value === String(this.value);
    },
    selectOption(opt) {
      if (this.multiple) {
        let arr = [...this.selectedValuesArray];
        const idx = arr.indexOf(opt.value);
        if (idx > -1) {
          arr.splice(idx, 1);
        } else {
          arr.push(opt.value);
        }
        const newVal = arr.length > 0 ? arr.join(',') : null;
        this.$emit('input', newVal);
        this.$emit('change', newVal);
      } else {
        this.$emit('input', opt.value);
        this.$emit('change', opt.value);
        this.isOpen = false;
      }
    },
    handleClickOutside(e) {
      if (this.$refs.selectWrapper && !this.$refs.selectWrapper.contains(e.target)) {
        this.isOpen = false;
      }
    }
  }
};
</script>

<style scoped>
.pd-select {
  position: relative !important;
  width: 100% !important;
  font-family: 'Inter', sans-serif !important;
}
.pd-select__control {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
  padding: 8px 12px !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  font-size: 11px !important;
  color: #334155 !important;
  transition: all 0.2s ease !important;
}
.pd-select:hover .pd-select__control {
  border-color: #CBD5E1 !important;
}
.pd-select--open .pd-select__control {
  border-color: #0A5C36 !important;
  box-shadow: 0 0 0 3px rgba(10,92,54,0.1) !important;
}
.pd-select__value {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  font-weight: 500 !important;
}
.pd-select__placeholder {
  color: #94A3B8 !important;
  font-weight: 400 !important;
}
.pd-select__actions {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
}
.pd-select__arrow, .pd-select__clear {
  font-size: 10px !important;
  color: #94A3B8 !important;
  transition: transform 0.2s ease, color 0.2s ease !important;
}
.pd-select__clear {
  cursor: pointer !important;
}
.pd-select__clear:hover {
  color: #EF4444 !important;
}
.pd-select--open .pd-select__arrow {
  transform: rotate(180deg) !important;
  color: #0A5C36 !important;
}
.pd-select__menu {
  position: absolute !important;
  top: calc(100% + 4px) !important;
  left: 0 !important;
  width: 100% !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
  z-index: 100 !important;
  max-height: 200px !important;
  overflow-y: auto !important;
}
/* Scrollbar custom */
.pd-select__menu::-webkit-scrollbar { width: 4px !important; }
.pd-select__menu::-webkit-scrollbar-track { background: transparent !important; }
.pd-select__menu::-webkit-scrollbar-thumb { background: #CBD5E1 !important; border-radius: 4px !important; }

.pd-select__menu-inner {
  padding: 4px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 2px !important;
}

/* Buscador interno */
.pd-select__search {
  display: flex !important;
  align-items: center !important;
  padding: 6px 10px !important;
  gap: 8px !important;
  border-bottom: 1px solid #E2E8F0 !important;
  margin-bottom: 4px !important;
  color: #94A3B8 !important;
}
.pd-select__search input {
  border: none !important;
  outline: none !important;
  width: 100% !important;
  font-size: 11px !important;
  color: #334155 !important;
  background: transparent !important;
}
.pd-select__empty {
  padding: 10px !important;
  text-align: center !important;
  font-size: 11px !important;
  color: #94A3B8 !important;
  font-style: italic !important;
}

.pd-select__option {
  padding: 8px 10px !important;
  font-size: 11px !important;
  color: #475569 !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  transition: background 0.15s !important;
}
.pd-select__option:hover {
  background: #F1F5F9 !important;
  color: #1E293B !important;
}
.pd-select__option--selected {
  background: #ECFDF5 !important;
  color: #059669 !important;
  font-weight: 600 !important;
}
.pd-select__check {
  font-size: 10px !important;
}

/* Animación */
.pd-fade-slide-enter-active, .pd-fade-slide-leave-active {
  transition: all 0.2s ease !important;
}
.pd-fade-slide-enter, .pd-fade-slide-leave-to {
  opacity: 0 !important;
  transform: translateY(-5px) !important;
}
</style>
